/**
 * Evidence Findings View
 *
 * Fetches evidence-driven findings from /api/playground/annotationInsights?flow=v2
 * Uses the dual-agent annotation pipeline (tenant's aiAnnotation.mode is non-legacy).
 * Includes a collapsible pipeline trace showing reasoning at each step.
 * Dataset multi-select dropdown in the header.
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  SkeletonLoader,
  UI_CAPTION,
  UI_CAPTION_SEMIBOLD,
  UI_TITLE,
  UI_TAG,
  colors2,
  icons,
  DropdownMulti,
  ButtonGhostCompact,
} from "oolib";
import { Tag, TagClear } from "oolib/dist/v2/components/Tags";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { useGetQueryData } from "../../utils/react-query-hooks/general";
import { isTplAnnotationEnabled } from "@okf/ootils";
import { DataVizWidgetShell } from "../../components/analysisModules/reports/ReportLanding/comps/WidgetRenderer/comps/DataVizWidgetShell";
import { InsightDetailLightbox } from "./InsightDetailLightbox";
import { getSentimentConfig } from "./insightConstants";

const { CaretRight } = icons;

// Derive dominant sentiment from V2 evidence array
const getDominantSentiment = (finding) => {
  const counts = { positive: 0, negative: 0, neutral: 0 };
  for (const e of finding.evidence || []) {
    const s = e.attributes?.sentiment_polarity?.[0]?.display?.toLowerCase();
    if (s && s in counts) counts[s]++;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] || "neutral";
};

const FindingRow = ({ finding, isActive, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const dominant = getDominantSentiment(finding);
  const { label: sentimentLabel, color: sentimentColor } = getSentimentConfig(dominant);

  return (
    <div
      style={{
        border: isActive ? `1px solid ${colors2.grey80}` : `1px solid ${colors2.grey5}`,
        borderRadius: "1.2rem",
        background: isActive ? colors2.grey2 : hovered ? colors2.grey2 : "transparent",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "1.6rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.8rem" }}>
            {/* <Tag display={sentimentLabel} fill="solid" typo={UI_TAG} style={{ background: sentimentColor }} /> */}
            {finding.metrics?.totalDocuments > 0 && (
              <Tag display={`${finding.metrics.totalDocuments} doc${finding.metrics.totalDocuments === 1 ? "" : "s"}`} fill="outline" typo={UI_TAG} />
            )}
          </div>
          <UI_TITLE>{finding.heading}</UI_TITLE>
          <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey60 }}>
            {finding.sentence}
          </UI_CAPTION_SEMIBOLD>
        </div>
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0, paddingTop: 2 }}>
          <CaretRight size={14} color={colors2.grey40} />
        </div>
      </div>
    </div>
  );
};

const FindingsSkeleton = () => (
  <div style={{ padding: "1rem", display: "flex", gap: "1rem", flexDirection: "column" }}>
    {[1, 2, 3].map((i) => (
      <div key={i} style={{ padding: "1.6rem", border: `1px solid ${colors2.grey5}`, borderRadius: "1.2rem" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: "0.8rem" }}>
          <SkeletonLoader style={{ height: 22, width: 70, borderRadius: 12 }} />
          <SkeletonLoader style={{ height: 22, width: 55, borderRadius: 12 }} />
        </div>
        <SkeletonLoader style={{ height: 18, width: "75%", borderRadius: 4, marginBottom: 8 }} />
        <SkeletonLoader style={{ height: 14, width: "90%", borderRadius: 4 }} />
      </div>
    ))}
  </div>
);

// ── Pipeline Trace ──────────────────────────────────────────────

const PipelineTrace = ({ trace, meta }) => {
  const [expanded, setExpanded] = useState(false);

  if (!trace?.length) return null;

  return (
    <div style={{ marginBottom: "1rem" }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          padding: "6px 0",
        }}
      >
        <UI_CAPTION style={{ fontWeight: 600, color: colors2.grey50 }}>
          {expanded ? "\u25BE" : "\u25B8"} Pipeline Trace
        </UI_CAPTION>
        {meta?.cached && (
          <Tag display="CACHED" fill="solid" size="XS" style={{ background: colors2.grey30, color: colors2.white, fontSize: 9 }} />
        )}
        {meta?.pipelineMs != null && (
          <UI_CAPTION color={colors2.grey30} style={{ fontSize: 11 }}>
            {(meta.pipelineMs / 1000).toFixed(1)}s
          </UI_CAPTION>
        )}
      </div>
      {expanded && (
        <div
          style={{
            background: colors2.grey2,
            borderRadius: 8,
            padding: "10px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {trace.map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <UI_CAPTION
                style={{
                  fontWeight: 700,
                  color: colors2.grey40,
                  minWidth: 20,
                  textAlign: "right",
                  flexShrink: 0,
                  fontSize: 11,
                }}
              >
                {t.step || "-"}
              </UI_CAPTION>
              <div style={{ flex: 1 }}>
                <UI_CAPTION style={{ fontWeight: 600, fontSize: 11 }}>
                  {t.title}
                  {t.ms > 0 && (
                    <span style={{ fontWeight: 400, color: colors2.grey30, marginLeft: 6 }}>
                      {t.ms}ms
                    </span>
                  )}
                </UI_CAPTION>
                <UI_CAPTION color={colors2.grey50} style={{ fontSize: 11, lineHeight: 1.4 }}>
                  {t.detail}
                </UI_CAPTION>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Dataset Selector ────────────────────────────────────────────

const DatasetSelector = ({ selectedDatasets, setSelectedDatasets, datasetOptions }) => {
  if (!datasetOptions?.length) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <DropdownMulti
        S
        popOutOfOverflowHiddenParent
        genCustomSelectComp={() => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 6,
              border: `1px solid ${colors2.grey10}`,
              cursor: "pointer",
              background: "white",
            }}
          >
            <UI_CAPTION style={{ fontWeight: 500, whiteSpace: "nowrap" }}>
              {selectedDatasets.length === datasetOptions.length
                ? "All Datasets"
                : `${selectedDatasets.length} Dataset${selectedDatasets.length !== 1 ? "s" : ""}`}
            </UI_CAPTION>
          </div>
        )}
        alignDropdown={"right"}
        options={datasetOptions}
        value={selectedDatasets}
        onChange={(id, val) => setSelectedDatasets(val)}
      />
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────

const fetchFindings = async (contentTypes, { forceRefresh = false } = {}) => {
  const params = { flow: "v2" };
  if (contentTypes?.length) {
    params.contentTypes = contentTypes.join(",");
  }
  if (forceRefresh) params.forceRefresh = "true";
  const res = await axios.get("/api/playground/annotationInsights", { params });
  return res.data;
};

export const EvidenceFindingsView = ({ debug = false }) => {
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [forceRefreshing, setForceRefreshing] = useState(false);
  const queryClient = useQueryClient();

  // Get all tpls — only show datasets that have annotation-enabled fields
  // Same check AnnotationExplorer uses: template must be publishing segment
  // AND have at least one block with annotation.enable: true
  const allTpls = useGetQueryData("allTpls") || [];
  const datasetOptions = useMemo(() => {
    return allTpls
      .filter(isTplAnnotationEnabled)
      .map((t) => ({
        display: t.general?.content?.title || t.kp_content_type,
        value: t.kp_content_type,
      }));
  }, [allTpls]);

  const [selectedDatasets, setSelectedDatasets] = useState([]);

  // Auto-select all datasets on first load
  useEffect(() => {
    if (datasetOptions.length > 0 && selectedDatasets.length === 0) {
      setSelectedDatasets(datasetOptions);
    }
  }, [datasetOptions]);

  // Build the contentTypes array from selection
  const selectedContentTypes = useMemo(() => {
    if (!selectedDatasets.length || selectedDatasets.length === datasetOptions.length) {
      return []; // empty = all (let backend auto-detect)
    }
    return selectedDatasets.map((d) => d.value);
  }, [selectedDatasets, datasetOptions]);

  // Re-fetch when dataset selection changes — pipeline re-runs on backend
  const queryKey = ["annotationInsightsV2", selectedContentTypes.sort().join(",")];
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => fetchFindings(selectedContentTypes),
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: selectedDatasets.length > 0,
  });

  const handleForceRefresh = async () => {
    setForceRefreshing(true);
    setSelectedFinding(null);
    try {
      const freshData = await fetchFindings(selectedContentTypes, { forceRefresh: true });
      queryClient.setQueryData(queryKey, freshData);
    } catch (e) {
      console.error("[ForceRefresh] failed:", e);
    } finally {
      setForceRefreshing(false);
    }
  };

  const findings = data?.findings || [];

  const headerRightComp = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {debug && (
        <ButtonGhostCompact
          onClick={!forceRefreshing ? handleForceRefresh : undefined}
          disable={forceRefreshing}
        >
            {forceRefreshing ? "Refreshing..." : "Force Refresh"}
        </ButtonGhostCompact>
      )}
      {datasetOptions.length > 1 && (
        <DatasetSelector
          selectedDatasets={selectedDatasets}
          setSelectedDatasets={setSelectedDatasets}
          datasetOptions={datasetOptions}
        />
      )}
    </div>
  );

  return (
    <>
      <DataVizWidgetShell
        title="Findings"
        // typo={<UI_BODY_BOLD style={{ color: colors2.grey80 }}>{title}</UI_BODY_BOLD>}
        disableAccordion
        customHeaderRightComp={headerRightComp}
        widgetWrapperStyle={{ padding: "0 1rem 1rem 1rem", flex: 1, minHeight: 0, overflow: "auto" }}
        style={{ flex: 1, minHeight: 0, width: "100%" }}
      >
        {(isLoading || forceRefreshing) && !data ? (
          <FindingsSkeleton />
        ) : isError ? (
          <UI_CAPTION color={colors2.red} style={{ padding: "1rem" }}>
            Failed to load findings
          </UI_CAPTION>
        ) : !findings?.length ? (
          <div style={{ padding: "1.5rem" }}>
            {debug && <PipelineTrace trace={data?.trace} meta={data?.meta} />}
            <div style={{ textAlign: "center" }}>
              <UI_CAPTION color={colors2.grey40}>
                No findings available yet. Findings are generated once
                enough annotations have been created.
              </UI_CAPTION>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {debug && <PipelineTrace trace={data.trace} meta={data.meta} />}
            {/* Selected dataset tags */}
            {selectedDatasets.length > 0 && selectedDatasets.length < datasetOptions.length && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {selectedDatasets.map((d) => (
                  <TagClear
                    key={d.value}
                    display={d.display}
                    size="XS"
                    onClear={() =>
                      setSelectedDatasets((prev) => prev.filter((p) => p.value !== d.value))
                    }
                  />
                ))}
              </div>
            )}
            {findings.map((finding) => (
              <FindingRow
                key={finding.id}
                finding={finding}
                isActive={selectedFinding?.id === finding.id}
                onClick={() =>
                  setSelectedFinding(
                    selectedFinding?.id === finding.id ? null : finding
                  )
                }
              />
            ))}
          </div>
        )}
      </DataVizWidgetShell>

      {selectedFinding && (
        <InsightDetailLightbox
          insight={selectedFinding}
          onClose={() => setSelectedFinding(null)}
        />
      )}
    </>
  );
};
